import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Dumbbell, Brain, MessageCircle, Camera } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <Navbar />
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <h1 className="font-heading text-6xl md:text-8xl font-black tracking-tighter uppercase mb-6">
              TRAIN <span className="gradient-text">SMARTER</span>
            </h1>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto mb-10">
              AI-powered workouts, personalized coaching, and real-time form analysis.
              Transform your fitness journey with cutting-edge technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/pricing")}
                className="bg-primary text-white px-10 py-4 rounded-full font-bold tracking-wide uppercase neon-glow hover:scale-105 transition-transform"
                data-testid="hero-cta"
              >
                Start Training
              </button>
              <button
                onClick={() => navigate("/login")}
                className="border border-[#1E293B] text-zinc-300 px-10 py-4 rounded-full font-semibold hover:bg-[#141B2D] transition-colors"
                data-testid="hero-login"
              >
                Sign In
              </button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mt-16 relative">
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/5">
              <img
                src="https://images.unsplash.com/photo-1643369283064-f9146f94c6c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
                alt="Gym workout"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
            </div>
          </motion.div>
        </div>
      </section>
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-center mb-16">
            YOUR AI FITNESS ARSENAL
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Brain, title: "AI Workout Generator", desc: "Personalized workouts based on your goals, equipment, and fitness level" },
              { icon: MessageCircle, title: "AI Coach Chatbot", desc: "24/7 expert guidance on form, nutrition, and training strategies" },
              { icon: Camera, title: "Form Analysis", desc: "Upload videos for real-time feedback on your exercise technique" },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-[#0B0F1A] border border-white/5 rounded-2xl p-8 hover:border-primary/50 transition-all duration-500"
              >
                <feature.icon className="w-12 h-12 text-primary mb-6" />
                <h3 className="font-heading text-2xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-text-secondary">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-24 px-6 bg-gradient-to-b from-transparent via-blue-900/10 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-8">READY TO TRANSFORM?</h2>
          <p className="text-xl text-text-secondary mb-10">Join thousands of athletes already training with AI-powered precision.</p>
          <button
            onClick={() => navigate("/pricing")}
            className="bg-primary text-white px-12 py-5 rounded-full font-bold text-lg tracking-wide uppercase neon-glow hover:scale-105 transition-transform"
            data-testid="cta-pricing"
          >
            View Pricing
          </button>
        </div>
      </section>
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <Dumbbell className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold">PALADIN</span>
          </div>
          <p className="text-text-muted text-sm">&copy; 2026 PaladinApp. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
